import { Component, computed, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { IRecetaData } from '@core/interfaces/entidades/receta.entidad';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';
import { IRecetaDetalleReq } from '@core/interfaces/request-response/receta.dto.interface';
import { RecetaApiService } from '@core/services/api/receta-api.service';
import { ProductoApiService } from '@core/services/api/producto.service';
import { ToastService } from '@shared/services/toast.service';
import { ETipoUnidadSUNAT } from '@core/enums/tipoUnidadSUNAT.enum';
import { convertirUnidad, unidadesCompatibles } from '@core/enums/unidad-compatibles.function';
import { ETipoProducto } from '@core/enums/tipoProducto.enum';
import { UiButton, UiInputNumber, UiSelect, type SelectOption } from '@shared/components/ui';

@Component({
  selector: 'app-receta-form',
  imports: [CommonModule, FormsModule, UiButton, UiInputNumber, UiSelect],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './receta-form.component.html',
})
export class RecetaForm implements OnInit {
  ngOnInit(): void {
    this.cargarDatosIniciales();
  }

  private recetaApiService = inject(RecetaApiService);
  private productoApiService = inject(ProductoApiService);
  private toastService = inject(ToastService);

  receta = input<IRecetaData | undefined>();
  modoEdicion = input<boolean>(false);
  modoDuplicar = input<boolean>(false);

  onGuardar = output<IRecetaData | undefined>();
  onCancelar = output<void>();

  productosReceta = signal<IProductoData[]>([]);
  productosIngrediente = signal<IProductoData[]>([]);
  guardando = signal(false);
  datosListos = signal(false);
  productoRecetaSeleccionado = signal<IProductoData | null>(null);
  nombreDuplicado = signal('');
  nombreEdicion = signal('');
  isCreacion = computed(() => !this.modoEdicion() && !this.modoDuplicar());

  readonly compareProductoById = (a: any, b: any) => a?.id === b?.id;

  opcionesProductosReceta = computed<SelectOption[]>(() =>
    this.productosReceta().map((p) => ({ label: `${p.nombre} (${p.codigo})`, value: p })),
  );

  opcionesProductosIngrediente = computed<SelectOption[]>(() =>
    this.productosIngrediente().map((p) => ({ label: `${p.nombre} (${p.codigo})`, value: p })),
  );

  ingredientes: {
    producto: IProductoData | null;
    cantidad: number;
    unidad: ETipoUnidadSUNAT;
    costoUnitario: number;
    tempId: number;
  }[] = [];

  private tempIdCounter = 0;

  unidadesMedida = [
    { value: ETipoUnidadSUNAT.GRAMO, label: 'Gramos (g)' },
    { value: ETipoUnidadSUNAT.KILOGRAMO, label: 'Kilogramos (kg)' },
    { value: ETipoUnidadSUNAT.MILILITRO, label: 'Mililitros (ml)' },
    { value: ETipoUnidadSUNAT.LITRO, label: 'Litros (L)' },
    { value: ETipoUnidadSUNAT.UNIDAD_BIENES, label: 'Unidades (u)' },
  ];

  unidadesCompatiblesParaIngrediente(ing: {
    producto: IProductoData | null;
    unidad: ETipoUnidadSUNAT;
  }): { value: ETipoUnidadSUNAT; label: string }[] {
    const ref = ing.producto?.unidad_medida || ing.unidad;
    const compatibles = unidadesCompatibles(ref);
    return this.unidadesMedida.filter((u) => compatibles.includes(u.value));
  }

  private cargarDatosIniciales() {
    forkJoin([
      this.productoApiService.paginar(1, 200, undefined, ETipoProducto.RECETA),
      this.productoApiService.paginar(1, 200, undefined, ETipoProducto.INGREDIENTE),
    ]).subscribe({
      next: ([recetasRes, ingredientesRes]) => {
        this.productosReceta.set(recetasRes.data.filter((p) => p.activo));
        this.productosIngrediente.set(ingredientesRes.data.filter((p) => p.activo));
        this.cargarRecetaEnEdicion();
        this.datosListos.set(true);
      },
      error: () => {
        this.datosListos.set(true);
      },
    });
  }

  private cargarRecetaEnEdicion() {
    const recetaData = this.receta();
    if (!recetaData) return;
    if (!this.modoEdicion() && !this.modoDuplicar()) return;

    if (this.modoDuplicar()) {
      this.nombreDuplicado.set(`${recetaData.nombre} (copia)`);
    }
    if (this.modoEdicion()) {
      this.nombreEdicion.set(recetaData.nombre);
    }

    this.ingredientes = recetaData.detalles.map((d) => {
      const productoMatch =
        this.productosIngrediente().find((p) => p.id === d.ingrediente_producto_id) ||
        ({
          id: d.ingrediente_producto_id,
          nombre: d.ingrediente_nombre,
          codigo: d.ingrediente_codigo,
          precio_base: d.costo_unitario,
          unidad_medida: d.ingrediente_unidad_medida || d.unidad_medida,
        } as IProductoData);
      return {
        producto: productoMatch,
        cantidad: d.cantidad_requerida,
        unidad: d.unidad_medida,
        costoUnitario: d.costo_unitario,
        tempId: this.tempIdCounter++,
      };
    });

    const productoEnLista = this.productosReceta().find(
      (p) => p.id === recetaData.producto_receta_id,
    );
    if (productoEnLista) {
      this.productoRecetaSeleccionado.set(productoEnLista);
    } else {
      this.productoRecetaSeleccionado.set({
        id: recetaData.producto_receta_id,
        nombre: recetaData.nombre,
        codigo: recetaData.producto_receta_codigo,
        precio_base: recetaData.costo_estimado,
        precio_venta: recetaData.producto_receta_precio_venta,
      } as IProductoData);
    }
  }

  cargarProductosReceta(termino?: string) {
    const t = termino?.trim();
    if (t) {
      this.productoApiService.buscarProducto(t).subscribe({
        next: (r) =>
          this.productosReceta.set(
            r.data.filter((p) => p.activo && p.tipo_producto === ETipoProducto.RECETA),
          ),
      });
    } else {
      this.productoApiService.paginar(1, 200, undefined, ETipoProducto.RECETA).subscribe({
        next: (r) => this.productosReceta.set(r.data.filter((p) => p.activo)),
      });
    }
  }

  agregarIngrediente() {
    this.ingredientes.push({
      producto: null,
      cantidad: 0,
      unidad: ETipoUnidadSUNAT.GRAMO,
      costoUnitario: 0,
      tempId: this.tempIdCounter++,
    });
  }

  removerIngrediente(index: number) {
    this.ingredientes.splice(index, 1);
  }

  onIngredienteSeleccionado(index: number) {
    const ing = this.ingredientes[index];
    if (ing.producto) {
      ing.unidad = ing.producto.unidad_medida;
      this.actualizarCostoIngrediente(index);
    }
  }

  onUnidadIngredienteCambiada(index: number) {
    this.actualizarCostoIngrediente(index);
  }

  private actualizarCostoIngrediente(index: number) {
    const ing = this.ingredientes[index];
    if (!ing.producto) return;
    const factor = convertirUnidad(1, ing.unidad, ing.producto.unidad_medida);
    ing.costoUnitario = ing.producto.precio_base * factor;
  }

  costoTotal(): number {
    return this.ingredientes.reduce((sum, ing) => {
      return sum + (ing.costoUnitario || 0) * (ing.cantidad || 0);
    }, 0);
  }

  guardar() {
    if (!this.datosListos()) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'Los datos aún se están cargando. Espera un momento.',
      );
      return;
    }

    if (this.isCreacion() && !this.productoRecetaSeleccionado()?.id) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'Selecciona un producto para la receta.',
      );
      return;
    }
    if (this.modoDuplicar() && !this.nombreDuplicado().trim()) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'El nombre de la copia es obligatorio.',
      );
      return;
    }
    if (this.isCreacion() && this.ingredientes.length === 0) {
      this.toastService.show('top-right', 'error', 'Error', 'Agrega al menos un ingrediente.');
      return;
    }
    for (let i = 0; i < this.ingredientes.length; i++) {
      if (!this.ingredientes[i].producto?.id) {
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          `Selecciona un ingrediente en la fila ${i + 1}.`,
        );
        return;
      }
      if (!this.ingredientes[i].cantidad || this.ingredientes[i].cantidad <= 0) {
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          `La cantidad del ingrediente ${i + 1} debe ser mayor a 0.`,
        );
        return;
      }
    }

    const detallesReq: IRecetaDetalleReq[] = this.ingredientes.map((ing) => ({
      ingrediente_producto_id: ing.producto!.id,
      cantidad_requerida: ing.cantidad,
      unidad_medida: ing.unidad,
    }));

    this.guardando.set(true);

    if (this.modoDuplicar() && this.receta()) {
      this.recetaApiService
        .duplicar(this.receta()!.producto_receta_id, {
          producto_receta_id: this.productoRecetaSeleccionado()!.id,
          nombre: this.nombreDuplicado(),
          detalles: detallesReq,
        })
        .subscribe({ next: (d) => this.handleSuccess(d), error: (e) => this.handleError(e) });
    } else if (this.modoEdicion() && this.receta()) {
      this.recetaApiService
        .editar(this.receta()!.producto_receta_id, {
          nombre: this.nombreEdicion() !== this.receta()!.nombre ? this.nombreEdicion() : undefined,
          detalles: detallesReq,
        })
        .subscribe({ next: (d) => this.handleSuccess(d), error: (e) => this.handleError(e) });
    } else {
      this.recetaApiService
        .crear({
          producto_receta_id: this.productoRecetaSeleccionado()!.id,
          detalles: detallesReq,
        })
        .subscribe({ next: (d) => this.handleSuccess(d), error: (e) => this.handleError(e) });
    }
  }

  private handleSuccess(data: IRecetaData) {
    this.guardando.set(false);
    const msg = this.modoEdicion()
      ? 'Receta actualizada correctamente.'
      : this.modoDuplicar()
        ? 'Receta duplicada correctamente.'
        : 'Receta creada correctamente.';
    this.toastService.show('top-right', 'success', 'Éxito', msg);
    this.onGuardar.emit(data);
  }

  private handleError(err: any) {
    this.guardando.set(false);
    if (err.status == 401) return;
    this.toastService.show(
      'top-right',
      'error',
      'Error',
      err.error.message ?? 'Error al guardar la receta.',
    );
  }

  cancelar() {
    this.onCancelar.emit();
  }
}
